<template>
    <div v-if="loading">
        <ScaleLoader />
    </div>
    <div v-else class="card">
        <div class="grid">
            <div class="form-field col-12 md:col-6">
                <label for="reportConfig">Description</label>
                <InputText
                    maxlength = "255"
                    id="description"
                    v-model="reportConfig.description"
                    placeholder="Configuration description"
                />
            </div>
            <div class="form-field col-12 md:col-6">
                <label for="validity">Validity <small>(in months)</small> <small style="color: red">*</small></label>
                <InputNumber
                    maxlength = "255"
                    id="validity"
                    v-model="reportConfig.validity"
                    placeholder="Assessments validity in months"
                />
            </div>
            <div class="form-field col-12 md:col-6">
                <label for="startDate">Start Date <small style="color: red">*</small></label>
                <Calendar inputId="startDate" v-model="startDate" dateFormat="dd-mm-yy"  :showIcon="true"/>
            </div>
            <div class="form-field col-12 md:col-6">
                <label for="endDate">Start Date <small style="color: red">*</small></label>
                <Calendar inputId="endDate" v-model="endDate" dateFormat="dd-mm-yy" :showIcon="true" />
            </div>
            <div class="form-field col-12">
                <div class="flex justify-content-center">
                    <Button
                        :disabled="invalidConfiguration"
                        label="Generate report"
                        :loading="loadingReport"
                        icon="pi pi-check"
                        class="ml-2 p-button-primary"
                        @click="onSubmit" />
                </div>
                <small v-if="invalidConfiguration" class="flex justify-content-center mt-3">
                    The Validity must be a positive integer number, you should select at least one Assessment and choose start date and end date.
                </small>
            </div>
            <div class="form-field col-12">
                <DataTable
                    v-model:selection="selectedAssessments"
                    :lazy="true"
                    :value="assessmentList"
                    :resizableColumns="true"
                    ref="dt"
                    data-key="id"
                    :rowHover="true"
                    :rows="10"
                    :loading="assessmentListLoading"
                    responsiveLayout="scroll"
                    @row-select-all="onAssessmentRowSelectedAll"
                    @row-unselect-all="onAssessmentRowUnselectedAll"
                    @row-select="onAssessmentRowSelect"
                    @row-unselect="onAssessmentRowUnselect"
                >
                    <template #empty>No Assessments available.</template>
                    <template #loading>Loading Assessments. Please wait.</template>

                    <Column selectionMode="multiple" style="max-width: 4rem" />
                    <Column field="name" header="Name" style="max-width: 14rem" />
                    <Column field="code" header="Code" style="max-width: 14rem" />
                    <Column field="description" header="Description" style="max-width: 14rem" />
                </DataTable>
            </div>

            <div v-if="reportLoaded" class="col-12">

                <div class="grid mt-3 mr-2 ml-2">
                    <div class="col-10 mt-3 mb-3">
                        As at {{reportData?.endDate?.setLocale('en-GB').toFormat('dd MMM yyyy')}}, how often are your staff required to undergo physical fitness testing?
                        (In months)
                    </div>
                    <div class="col-2 mt-3 mb-3 flex justify-content-end"><b>{{reportData.frequency}}</b></div>

                    <div class="col-12 mb-3">
                        How many staff (headcount) undertook the required number of fitness tests), between
                        {{reportData?.startDate?.setLocale('en-GB').toFormat('dd MMM yyyy')}} -
                        {{reportData?.endDate?.setLocale('en-GB').toFormat('dd MMM yyyy')}}?
                    </div>

                    <div class="col-5 border-top-1 border-300">
                        Duty system
                    </div>
                    <template  v-for="(item, index) in reportData.allTests?.duty" :key="item">
                        <div :class="` border-top-1 border-300 col-5 ${(index > 0) ? 'col-offset-5' : '' }`">{{getDutyLabel(item?.duty)}} </div>
                        <div class=" border-top-1 border-300 col-2  flex justify-content-end"><b>{{item?.user_count}}</b></div>
                    </template>
                    <div v-if="reportData.allTests?.duty.length === 0" class="border-top-1 border-300 col-7">
                        No user found
                    </div>

                    <div class="col-5  border-top-1 border-300">
                        Gender
                    </div>
                    <template  v-for="(item, index) in reportData.allTests?.sex" :key="item">
                        <div :class="` border-top-1 border-300 col-5 ${(index > 0) ? 'col-offset-5' : '' }`">{{getSexLabel(item?.sex)}} </div>
                        <div class="  border-top-1 border-300 col-2  flex justify-content-end"><b>{{item?.user_count}}</b></div>
                    </template>
                    <div v-if="reportData.allTests?.sex.length === 0" class="border-top-1 border-300 col-7">
                        No user found
                    </div>

                    <div class="col-5 border-top-1 border-300">
                        Age band
                    </div>
                    <template  v-for="(item, index) in reportData.allTests?.age_band" :key="item">
                        <div :class="` border-top-1 border-300 col-5 ${(index > 0) ? 'col-offset-5' : '' }`">{{item?.age_band}} </div>
                        <div class=" border-top-1 border-300 col-2  flex justify-content-end"><b>{{item?.user_count}}</b></div>
                    </template>
                    <div v-if="reportData.allTests?.age_band.length === 0" class="border-top-1 border-300 col-7">
                        No user found
                    </div>

                    <div class="col-12 mt-4 mb-3">
                        How many staff (headcount) failed (at least one of) their required fitness tests), between
                        {{reportData?.startDate?.setLocale('en-GB').toFormat('dd MMM yyyy')}} -
                        {{reportData?.endDate?.setLocale('en-GB').toFormat('dd MMM yyyy')}}?
                    </div>

                    <div class="col-5 border-top-1 border-300">
                        Duty system
                    </div>
                    <template  v-for="(item, index) in reportData.fail?.duty" :key="item">
                        <div :class="` border-top-1 border-300 col-5 ${(index > 0) ? 'col-offset-5' : '' }`">{{getDutyLabel(item?.duty)}} </div>
                        <div class=" border-top-1 border-300 col-2  flex justify-content-end"><b>{{item?.user_count}}</b></div>
                    </template>
                    <div v-if="reportData.fail?.duty.length === 0" class="border-top-1 border-300 col-7">
                        No user found
                    </div>

                    <div class="col-5 border-top-1 border-300">
                        Gender
                    </div>
                    <template  v-for="(item, index) in reportData.fail?.sex" :key="item">
                        <div :class="` border-top-1 border-300 col-5 ${(index > 0) ? 'col-offset-5' : '' }`">{{getSexLabel(item?.sex)}} </div>
                        <div class=" border-top-1 border-300 col-2  flex justify-content-end"><b>{{item?.user_count}}</b></div>
                    </template>
                    <div v-if="reportData.fail?.sex.length === 0" class="border-top-1 border-300 col-7">
                        No user found
                    </div>

                    <div class="col-5 border-top-1 border-300">
                        Age band
                    </div>
                    <template  v-for="(item, index) in reportData.fail?.age_band" :key="item">
                        <div :class="` border-top-1 border-300 col-5 ${(index > 0) ? 'col-offset-5' : '' }`">{{item?.age_band}} </div>
                        <div class="col-2 border-top-1 border-300  flex justify-content-end"><b>{{item?.user_count}}</b></div>
                    </template>
                    <div v-if="reportData.fail?.age_band.length === 0" class="border-top-1 border-300 col-7">
                        No user found
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>

import { computed, onMounted, ref } from 'vue';
import useUserComposable from '@/composables/UserComposable';
import GlobalRequest from '@/api/global-request';
import authApi from '@/api/axios-api';
import { DateTime } from 'luxon';
const { company_id } = useUserComposable();

const reportConfig = ref({
    description: '',
    company_id: company_id,
    validity: 6,
    assessments: [],
    id: 0
})

const assessmentList = ref([])
const assessmentListLoading = ref(false)

const loading = ref(false)
const loadingReport = ref(false)

const startDate = ref(null)
const endDate = ref(null)

const reportData = ref({
    allTests: {},
    fail: {},
    frequency: 0,
    labels: {},
    validity: 0,
    endDate: DateTime.now(),
    startDate: DateTime.now(),
})

function getSexLabel(sexCode) {
    const data = reportData.value.labels.sex.find((sex) => {
        return parseInt(sex.value) === parseInt(sexCode) ? sex : ''
    });

    return data ? data?.label : 'N.A.'
}

function getDutyLabel(dutyCode) {
    const data = reportData.value.labels.duty.find((duty) => {
        return parseInt(duty.value) === parseInt(dutyCode) ? duty : ''
    });

    return data?.label
}

const reportLoaded = ref(false)

const invalidConfiguration = computed(() => {
    return (!reportConfig.value.validity || reportConfig.value.assessments.length === 0 || !startDate.value || !endDate.value)
})

const selectedAssessments = computed({
    get: () => assessmentList.value.filter(item => reportConfig.value.assessments.includes(item.code)),
    set: (newSelection) => {
        reportConfig.value.assessments = newSelection.map(item => item.code);
    },
});

onMounted(() => {
    loadLastConfiguration()
    loadAssessments()
})

function onSubmit() {
    console.log('onSubmit', reportConfig.value)

    loadingReport.value = true

    // save configuration
    if (reportConfig.value.id > 0) {
        authApi.patch(`/v2/report/hmi/insight_portfolio/configuration/${reportConfig.value.id}`, reportConfig.value).then((response) => {
            if (response.status === 200) {
                if (Object.hasOwn(response, 'data')) {
                    authApi.get('/v2/report/hmi/insight_portfolio/report', {
                        params: {
                            company_id: company_id.value,
                            configuration_id: response.data.data.id,
                            start_date: startDate.value,
                            end_date: endDate.value,
                        }
                    }).then(response => {
                        console.log('response', response)
                        parseReport(response.data.data)
                    }).catch(error => {
                        console.log('error', error);
                    }).finally(() => {
                        loadingReport.value = false
                    })
                }
            }
        }).catch(() => {
            loadingReport.value = false
        });
    } else {
        GlobalRequest.postRequest(`/v2/report/hmi/insight_portfolio/configuration`, reportConfig.value).then((response) => {
            if (response.statusCode === 201) {
                if (Object.hasOwn(response, 'data')) {
                    authApi.get('/v2/report/hmi/insight_portfolio/report', {
                        params: {
                            company_id: company_id.value,
                            configuration_id: response.data.data.id,
                            start_date: startDate.value,
                            end_date: endDate.value,
                        }
                    }).then(response => {
                        console.log('response', response)
                        parseReport(response.data.data)
                    }).catch(error => {
                        console.log('error', error);
                    }).finally(() => {
                        loadingReport.value = false
                    })
                }
            }
        }).catch(() => {
            loadingReport.value = false
        });
    }
}


function parseConfigurationResponse(response) {
    if (response.data.validity) {
        reportConfig.value.validity = response.data.validity
    }

    reportConfig.value.assessments = []

    if (response.data.details) {
        response.data.details.forEach(detail => {
            reportConfig.value.assessments.push(detail.assessment)
        })
    }

    if (response.data.description) {
        reportConfig.value.description = response.data.description
    }

    if (response.data.id) {
        reportConfig.value.id = response.data.id
    }
}

function parseReport(data) {
    console.log('data', data)

    reportData.value = {
        allTests: data.allTests,
        fail: data.fail,
        frequency: data.frequency,
        labels: data.labels,
        validity: data.validity,
        endDate: DateTime.fromFormat(data.endDate, 'yyyy-MM-dd'),
        startDate: DateTime.fromFormat(data.startDate, 'yyyy-MM-dd'),
    }

    reportLoaded.value = true

    DateTime
}

function loadLastConfiguration() {

    loading.value = true

    let params = {
        last: true,
        company_id: company_id.value,
    }
    return GlobalRequest.getRequest(`/v2/report/hmi/insight_portfolio/configuration`, {
        params: params
    }).then((response) => {
        if (response.statusCode === 200) {
            if (Object.hasOwn(response, 'data')) {
                parseConfigurationResponse(response)
                // if no previous configuration is available, an empty response will be returned.
            }
        }
    }).finally(() => {
        loading.value = false
    })
}

function loadAssessments() {
    assessmentList.value = []
    assessmentListLoading.value = true

    let params = {
        default: true,
        no_user_profile: true,
        company_id: company_id.value,
    }

    return GlobalRequest.getRequest(`/v2/template/assessment`, {
        params: params
    }).then((response) => {
        if (response.statusCode === 200) {
            if (Object.hasOwn(response, 'data')) {

                assessmentListLoading.value = false
                assessmentList.value = response.data
            }
        }
    })
}

// Chip - user remove
// function removeSelectedUser(unselectedUser) {
//     selectedAssessments.value = selectedAssessments.value.filter((item) => item.id !== unselectedUser.id)
// }

function onAssessmentRowSelectedAll(event) {
    if (Object.hasOwn(event, 'data') && Array.isArray(event.data)) {
        event.data.forEach((assessment) => {
            if (!reportConfig.value.assessments.find((item) => item.code === assessment.code)) {
                reportConfig.value.assessments.push(assessment.code)
            }
        })
    }
}

function onAssessmentRowUnselectedAll() {
    assessmentList.value.forEach((assessment) => {
        reportConfig.value.assessments = reportConfig.value.assessments.filter((item) => item.code !== assessment.code)
    })
}

// User list - user added by checkbox
function onAssessmentRowSelect(event) {
    if (Object.hasOwn(event, 'data')) {
        reportConfig.value.assessments.push(event.data.code)
    }
}

// User list - user removed by checkbox
function onAssessmentRowUnselect(event) {
    reportConfig.value.assessments = reportConfig.value.assessments.filter((item) => item.code !== event.data.code)
}

</script>

<style lang="scss" scoped>
.form-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5rem;
}

.form-field label {
    margin-bottom: 0.5rem;
}
</style>